/**
 * EventStockManagement - Comprehensive stock management with scan in/out and meal integration
 * Features: Barcode scanning, manual entry, meal deduction, transaction history, low stock alerts
 */
import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import { financeAPI, stockScanAPI } from '@/lib/api';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';

// Icons
import {
  Package, Plus, Scan, ArrowUpFromLine, ArrowDownToLine, History,
  AlertTriangle, Search, RefreshCw, Loader2, CheckCircle2, 
  Edit2, Trash2, Barcode, Box, TrendingDown, Utensils, FileText,
  Download, Filter
} from 'lucide-react';

const STOCK_CATEGORIES = [
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'merchandise', label: 'Merchandise' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'other', label: 'Other' }
];

const UNITS = ['pieces', 'kg', 'g', 'liters', 'ml', 'boxes', 'packs', 'bottles', 'cans', 'bags', 'servings'];

const STOCK_IN_REASONS = [
  { value: 'received', label: 'Received (New Stock)' },
  { value: 'returned', label: 'Returned' },
  { value: 'adjustment', label: 'Inventory Adjustment' },
  { value: 'transferred', label: 'Transferred In' }
];

const STOCK_OUT_REASONS = [
  { value: 'used', label: 'Used/Consumed' },
  { value: 'meal_prep', label: 'Meal Preparation' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'expired', label: 'Expired' },
  { value: 'transferred', label: 'Transferred Out' },
  { value: 'adjustment', label: 'Inventory Adjustment' }
];

const EventStockManagement = ({ eventId }) => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const scanInputRef = useRef(null);
  
  // State
  const [activeTab, setActiveTab] = useState('inventory');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [scanMode, setScanMode] = useState('out'); // 'in' or 'out'
  const [scanInput, setScanInput] = useState('');
  const [scannedItem, setScannedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Form states
  const [newItemForm, setNewItemForm] = useState({
    name: '', category: 'food_beverage', sku: '', barcode: '',
    quantity: 0, unit: 'pieces', unit_cost: 0, unit_price: 0,
    min_quantity: 0, location: '', supplier: '', description: ''
  });
  
  const [scanForm, setScanForm] = useState({
    quantity: 1, reason: 'used', reference: '', notes: '', location: ''
  });

  // Fetch stock items
  const { data: stockData, isLoading: stockLoading, refetch: refetchStock } = useQuery({
    queryKey: ['finance-stock', eventId, filterCategory],
    queryFn: async () => {
      const res = await financeAPI.listStock(token, eventId, filterCategory !== 'all' ? filterCategory : undefined);
      return res.data;
    },
    enabled: !!eventId && !!token
  });

  // Fetch low stock items
  const { data: lowStockItems } = useQuery({
    queryKey: ['finance-stock-low', eventId],
    queryFn: async () => {
      const res = await stockScanAPI.getLowStock(eventId);
      return res.data;
    },
    enabled: !!eventId && !!token
  });

  // Fetch all transactions
  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ['finance-stock-transactions', eventId],
    queryFn: async () => {
      const res = await stockScanAPI.getAllTransactions(eventId, { limit: 100 });
      return res.data;
    },
    enabled: !!eventId && !!token && activeTab === 'transactions'
  });

  // Fetch meal usage
  const { data: mealUsageData } = useQuery({
    queryKey: ['finance-stock-meal-usage', eventId],
    queryFn: async () => {
      const res = await stockScanAPI.getMealUsage(eventId, {});
      return res.data;
    },
    enabled: !!eventId && !!token && activeTab === 'meal-usage'
  });

  // Create stock item mutation
  const createMutation = useMutation({
    mutationFn: (data) => financeAPI.createStock(token, eventId, data),
    onSuccess: () => {
      toast.success('Stock item created');
      setShowAddDialog(false);
      resetNewItemForm();
      queryClient.invalidateQueries(['finance-stock', eventId]);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create item')
  });

  // Update stock item mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => financeAPI.updateStock(token, eventId, id, data),
    onSuccess: () => {
      toast.success('Stock item updated');
      setEditingItem(null);
      queryClient.invalidateQueries(['finance-stock', eventId]);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update item')
  });

  // Delete stock item mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => financeAPI.deleteStock(token, eventId, id),
    onSuccess: () => {
      toast.success('Stock item deleted');
      queryClient.invalidateQueries(['finance-stock', eventId]);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to delete item')
  });

  // Scan/lookup mutation
  const lookupMutation = useMutation({
    mutationFn: (barcode) => stockScanAPI.lookup(eventId, barcode),
    onSuccess: (res) => {
      if (res.data.found) {
        setScannedItem(res.data.item);
        toast.success(`Found: ${res.data.item.name}`);
      } else {
        toast.error(res.data.message || 'Item not found');
        setScannedItem(null);
      }
    },
    onError: () => {
      toast.error('Lookup failed');
      setScannedItem(null);
    }
  });

  // Stock in/out mutation
  const stockTransactionMutation = useMutation({
    mutationFn: (data) => stockScanAPI.scan(eventId, data),
    onSuccess: (res) => {
      const data = res.data;
      toast.success(data.message);
      
      if (data.low_stock_warning) {
        toast.warning('Low stock warning!', {
          description: `Only ${data.new_quantity} remaining (min: ${data.min_quantity})`
        });
      }
      
      // Update scanned item
      if (data.item) {
        setScannedItem({ ...data.item, quantity: data.new_quantity });
      }
      
      queryClient.invalidateQueries(['finance-stock', eventId]);
      queryClient.invalidateQueries(['finance-stock-transactions', eventId]);
      queryClient.invalidateQueries(['finance-stock-low', eventId]);
      
      // Reset form for next scan
      setScanForm({ ...scanForm, quantity: 1, reference: '', notes: '' });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Transaction failed')
  });

  // Handlers
  const resetNewItemForm = () => {
    setNewItemForm({
      name: '', category: 'food_beverage', sku: '', barcode: '',
      quantity: 0, unit: 'pieces', unit_cost: 0, unit_price: 0,
      min_quantity: 0, location: '', supplier: '', description: ''
    });
  };

  const handleScanLookup = () => {
    if (!scanInput.trim()) {
      toast.error('Enter a barcode, SKU, or item name');
      return;
    }
    lookupMutation.mutate(scanInput.trim());
  };

  const handleScanKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleScanLookup();
    }
  };

  const handleStockTransaction = () => {
    if (!scannedItem) {
      toast.error('Scan or select an item first');
      return;
    }
    if (scanForm.quantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }

    stockTransactionMutation.mutate({
      item_id: scannedItem.id,
      direction: scanMode,
      reason: scanForm.reason,
      quantity: scanForm.quantity,
      reference: scanForm.reference,
      notes: scanForm.notes,
      location: scanForm.location
    });
  };

  const handleSelectItemForScan = (item) => {
    setScannedItem(item);
    setShowScanDialog(true);
  };

  // Filter stock items
  const stockItems = stockData || [];
  const filteredItems = stockItems.filter(item =>
    !searchTerm ||
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.barcode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Focus scan input when dialog opens
  useEffect(() => {
    if (showScanDialog && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [showScanDialog]);

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold">{stockItems.length}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Low Stock Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <span className="text-2xl font-bold text-amber-600">{lowStockItems?.length || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold">
                ${stockItems.reduce((sum, item) => sum + (item.quantity * item.unit_cost || 0), 0).toLocaleString()}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setShowScanDialog(true)}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Quick Scan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Scan className="h-5 w-5 text-purple-500" />
              <span className="text-lg font-medium">Scan In/Out</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <TabsList>
            <TabsTrigger value="inventory" data-testid="inventory-tab">
              <Package className="h-4 w-4 mr-2" />
              Inventory
            </TabsTrigger>
            <TabsTrigger value="transactions" data-testid="transactions-tab">
              <History className="h-4 w-4 mr-2" />
              Transactions
            </TabsTrigger>
            <TabsTrigger value="meal-usage" data-testid="meal-usage-tab">
              <Utensils className="h-4 w-4 mr-2" />
              Meal Usage
            </TabsTrigger>
          </TabsList>

          <div className="flex gap-2">
            <Button onClick={() => setShowScanDialog(true)} variant="outline">
              <Scan className="h-4 w-4 mr-2" />
              Scan In/Out
            </Button>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        </div>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, SKU, or barcode..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {STOCK_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={refetchStock}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Stock Table */}
          <Card>
            <CardContent className="p-0">
              {stockLoading ? (
                <div className="p-8"><Skeleton className="h-64" /></div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>No stock items found</p>
                  <Button variant="link" onClick={() => setShowAddDialog(true)}>
                    Add your first item
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>SKU/Barcode</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Unit Cost</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{item.name}</div>
                            {item.location && (
                              <div className="text-xs text-muted-foreground">📍 {item.location}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {item.sku && <div>SKU: {item.sku}</div>}
                            {item.barcode && <div className="text-muted-foreground">
                              <Barcode className="h-3 w-3 inline mr-1" />{item.barcode}
                            </div>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {item.category?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={item.quantity <= (item.min_quantity || 0) ? 'text-red-600 font-bold' : ''}>
                            {item.quantity} {item.unit}
                          </span>
                          {item.min_quantity > 0 && (
                            <div className="text-xs text-muted-foreground">Min: {item.min_quantity}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          ${(item.unit_cost || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {item.quantity <= (item.min_quantity || 0) && item.min_quantity > 0 ? (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Low Stock
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">In Stock</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSelectItemForScan(item)}
                              title="Stock In/Out"
                            >
                              <Scan className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingItem(item)}
                              title="Edit"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => {
                                if (confirm('Delete this item?')) {
                                  deleteMutation.mutate(item.id);
                                }
                              }}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle>Stock Transactions</CardTitle>
              <CardDescription>Recent stock movements and adjustments</CardDescription>
            </CardHeader>
            <CardContent>
              {transactionsLoading ? (
                <Skeleton className="h-64" />
              ) : !transactionsData?.transactions?.length ? (
                <div className="text-center py-12 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>No transactions yet</p>
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <ArrowDownToLine className="h-6 w-6 mx-auto text-green-600 mb-1" />
                      <div className="text-2xl font-bold text-green-600">{transactionsData.summary?.total_in || 0}</div>
                      <div className="text-xs text-green-700">Total In</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg text-center">
                      <ArrowUpFromLine className="h-6 w-6 mx-auto text-red-600 mb-1" />
                      <div className="text-2xl font-bold text-red-600">{transactionsData.summary?.total_out || 0}</div>
                      <div className="text-xs text-red-700">Total Out</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <Box className="h-6 w-6 mx-auto text-blue-600 mb-1" />
                      <div className="text-2xl font-bold text-blue-600">{transactionsData.summary?.net_change || 0}</div>
                      <div className="text-xs text-blue-700">Net Change</div>
                    </div>
                  </div>

                  {/* Transaction List */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date/Time</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactionsData.transactions.map((tx, i) => (
                        <TableRow key={tx.id || i}>
                          <TableCell className="text-sm">
                            {new Date(tx.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="font-medium">{tx.item_name}</TableCell>
                          <TableCell>
                            <Badge variant={tx.direction === 'in' ? 'default' : 'secondary'} className={tx.direction === 'in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {tx.direction === 'in' ? <ArrowDownToLine className="h-3 w-3 mr-1" /> : <ArrowUpFromLine className="h-3 w-3 mr-1" />}
                              {tx.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={tx.direction === 'in' ? 'text-green-600' : 'text-red-600'}>
                              {tx.direction === 'in' ? '+' : '-'}{tx.quantity}
                            </span>
                            <div className="text-xs text-muted-foreground">
                              {tx.quantity_before} → {tx.quantity_after}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {tx.reference || tx.notes || '-'}
                          </TableCell>
                          <TableCell className="text-sm">{tx.created_by_name || 'System'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Meal Usage Tab */}
        <TabsContent value="meal-usage">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Utensils className="h-5 w-5" />
                Stock Usage by Meals
              </CardTitle>
              <CardDescription>Track which items are consumed during meal preparation</CardDescription>
            </CardHeader>
            <CardContent>
              {!mealUsageData?.by_item?.length ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Utensils className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>No meal-related stock usage recorded yet</p>
                  <p className="text-sm mt-2">Stock will be automatically deducted when meals are prepared</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* By Item Summary */}
                  <div>
                    <h4 className="font-medium mb-3">Items Used</h4>
                    <div className="space-y-2">
                      {mealUsageData.by_item.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <span className="font-medium">{item.item_name}</span>
                          <Badge variant="secondary">{item.total_used} used</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Stock Item
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Item Name *</Label>
                <Input
                  value={newItemForm.name}
                  onChange={(e) => setNewItemForm({ ...newItemForm, name: e.target.value })}
                  placeholder="e.g., Bottled Water"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={newItemForm.category} onValueChange={(v) => setNewItemForm({ ...newItemForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STOCK_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={newItemForm.unit} onValueChange={(v) => setNewItemForm({ ...newItemForm, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNITS.map(unit => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>SKU</Label>
                <Input
                  value={newItemForm.sku}
                  onChange={(e) => setNewItemForm({ ...newItemForm, sku: e.target.value })}
                  placeholder="Stock keeping unit"
                />
              </div>

              <div className="space-y-2">
                <Label>Barcode</Label>
                <Input
                  value={newItemForm.barcode}
                  onChange={(e) => setNewItemForm({ ...newItemForm, barcode: e.target.value })}
                  placeholder="For scanning"
                />
              </div>

              <div className="space-y-2">
                <Label>Initial Quantity</Label>
                <Input
                  type="number"
                  value={newItemForm.quantity}
                  onChange={(e) => setNewItemForm({ ...newItemForm, quantity: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Min. Quantity (Alert)</Label>
                <Input
                  type="number"
                  value={newItemForm.min_quantity}
                  onChange={(e) => setNewItemForm({ ...newItemForm, min_quantity: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Unit Cost ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newItemForm.unit_cost}
                  onChange={(e) => setNewItemForm({ ...newItemForm, unit_cost: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Selling Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newItemForm.unit_price}
                  onChange={(e) => setNewItemForm({ ...newItemForm, unit_price: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Storage Location</Label>
                <Input
                  value={newItemForm.location}
                  onChange={(e) => setNewItemForm({ ...newItemForm, location: e.target.value })}
                  placeholder="e.g., Warehouse A, Shelf 3"
                />
              </div>

              <div className="space-y-2">
                <Label>Supplier</Label>
                <Input
                  value={newItemForm.supplier}
                  onChange={(e) => setNewItemForm({ ...newItemForm, supplier: e.target.value })}
                  placeholder="Supplier name"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={newItemForm.description}
                  onChange={(e) => setNewItemForm({ ...newItemForm, description: e.target.value })}
                  placeholder="Additional details..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={() => createMutation.mutate(newItemForm)} disabled={createMutation.isPending || !newItemForm.name}>
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan In/Out Dialog */}
      <Dialog open={showScanDialog} onOpenChange={(open) => {
        setShowScanDialog(open);
        if (!open) {
          setScannedItem(null);
          setScanInput('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan className="h-5 w-5" />
              Stock In/Out
            </DialogTitle>
          </DialogHeader>

          {/* Mode Toggle */}
          <Tabs value={scanMode} onValueChange={(v) => {
            setScanMode(v);
            setScanForm({ ...scanForm, reason: v === 'in' ? 'received' : 'used' });
          }} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="out" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
                <ArrowUpFromLine className="h-4 w-4 mr-2" />
                Stock OUT
              </TabsTrigger>
              <TabsTrigger value="in" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">
                <ArrowDownToLine className="h-4 w-4 mr-2" />
                Stock IN
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-4 py-2">
            {/* Scan/Search Input */}
            <div className="space-y-2">
              <Label>Scan Barcode or Search</Label>
              <div className="flex gap-2">
                <Input
                  ref={scanInputRef}
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyPress={handleScanKeyPress}
                  placeholder="Scan barcode or enter SKU/name..."
                  className="flex-1"
                />
                <Button onClick={handleScanLookup} disabled={lookupMutation.isPending}>
                  {lookupMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Scanned Item Info */}
            {scannedItem && (
              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-bold text-lg">{scannedItem.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {scannedItem.sku && `SKU: ${scannedItem.sku}`}
                      {scannedItem.barcode && ` | Barcode: ${scannedItem.barcode}`}
                    </div>
                  </div>
                  <Badge variant="outline">{scannedItem.category?.replace('_', ' ')}</Badge>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-muted-foreground">Current Stock:</span>
                  <span className={`font-bold ${scannedItem.quantity <= (scannedItem.min_quantity || 0) ? 'text-red-600' : 'text-green-600'}`}>
                    {scannedItem.quantity} {scannedItem.unit}
                  </span>
                </div>
              </div>
            )}

            {/* Transaction Form */}
            {scannedItem && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={scanForm.quantity}
                      onChange={(e) => setScanForm({ ...scanForm, quantity: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Reason</Label>
                    <Select value={scanForm.reason} onValueChange={(v) => setScanForm({ ...scanForm, reason: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(scanMode === 'in' ? STOCK_IN_REASONS : STOCK_OUT_REASONS).map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Reference (Optional)</Label>
                  <Input
                    value={scanForm.reference}
                    onChange={(e) => setScanForm({ ...scanForm, reference: e.target.value })}
                    placeholder="PO number, meal service, etc."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Input
                    value={scanForm.notes}
                    onChange={(e) => setScanForm({ ...scanForm, notes: e.target.value })}
                    placeholder="Additional notes..."
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScanDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleStockTransaction} 
              disabled={!scannedItem || stockTransactionMutation.isPending}
              className={scanMode === 'in' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {stockTransactionMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : scanMode === 'in' ? (
                <ArrowDownToLine className="h-4 w-4 mr-2" />
              ) : (
                <ArrowUpFromLine className="h-4 w-4 mr-2" />
              )}
              {scanMode === 'in' ? 'Add Stock' : 'Remove Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      {editingItem && (
        <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Stock Item</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Item Name</Label>
                  <Input
                    value={editingItem.name}
                    onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>SKU</Label>
                  <Input
                    value={editingItem.sku || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, sku: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Barcode</Label>
                  <Input
                    value={editingItem.barcode || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, barcode: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Current Quantity</Label>
                  <Input
                    type="number"
                    value={editingItem.quantity}
                    onChange={(e) => setEditingItem({ ...editingItem, quantity: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Min. Quantity</Label>
                  <Input
                    type="number"
                    value={editingItem.min_quantity || 0}
                    onChange={(e) => setEditingItem({ ...editingItem, min_quantity: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingItem.unit_cost || 0}
                    onChange={(e) => setEditingItem({ ...editingItem, unit_cost: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={editingItem.location || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, location: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingItem(null)}>Cancel</Button>
              <Button onClick={() => updateMutation.mutate({ id: editingItem.id, data: editingItem })} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EventStockManagement;
